import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';

const Usage: React.FC = () => {
  const [startDate, setStartDate] = useState('2025-01-01');
  const [endDate, setEndDate] = useState('2025-01-31');

  // Mock data
  const usageData = [
    { date: '2025-01-15', requests: 1250, screenshots: 1200, failed: 50, cost: '$25.00' },
    { date: '2025-01-14', requests: 980, screenshots: 950, failed: 30, cost: '$19.60' },
    { date: '2025-01-13', requests: 1450, screenshots: 1430, failed: 20, cost: '$29.00' },
    { date: '2025-01-12', requests: 750, screenshots: 740, failed: 10, cost: '$15.00' },
    { date: '2025-01-11', requests: 890, screenshots: 870, failed: 20, cost: '$17.80' },
  ];

  const totalRequests = usageData.reduce((sum, item) => sum + item.requests, 0);
  const totalScreenshots = usageData.reduce((sum, item) => sum + item.screenshots, 0);
  const totalFailed = usageData.reduce((sum, item) => sum + item.failed, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Usage Statistics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Monitor your API usage and costs
      </Typography>

      {/* Date Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Filter by Date Range
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Requests
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {totalRequests.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Successful
            </Typography>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {totalScreenshots.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Failed
            </Typography>
            <Typography variant="h4" fontWeight={700} color="error.main">
              {totalFailed}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Success Rate
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {((totalScreenshots / totalRequests) * 100).toFixed(1)}%
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Usage Table */}
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ px: { xs: 1, sm: 0 } }}>
            Daily Usage Breakdown
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Date</strong></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Total Requests</strong></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Screenshots</strong></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Failed</strong></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Cost</strong></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageData.map((row) => (
                    <TableRow key={row.date}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                      <TableCell align="right">{row.requests.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.screenshots.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.failed}</TableCell>
                      <TableCell align="right">{row.cost}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label="Active" 
                          color="success" 
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Usage;

